import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Section = ({ label, children }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-700 mb-1.5">{label}</p>
    {children}
  </div>
);

export const ReviewDialog = ({ reviewId, onClose }) => {
  const [review, setReview] = useState(null);
  const [summary, setSummary] = useState("");
  const [rating, setRating] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/reviews/${reviewId}`).then((r) => {
      setReview(r.data);
      const d = r.data.final || r.data.draft || {};
      setSummary(d.performance_summary || "");
      setRating(String(d.rating ?? d.suggested_rating ?? ""));
    });
  }, [reviewId]);

  const finalized = review?.status === "finalized";
  const content = review ? (review.final || review.draft || {}) : {};
  const ev = review?.evidence;

  const saveEdits = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/reviews/${reviewId}`, {
        performance_summary: summary,
        suggested_rating: rating ? Number(rating) : undefined,
      });
      setReview((prev) => ({ ...prev, ...r.data }));
      toast.success("Draft updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const finalize = async () => {
    setBusy(true);
    try {
      await api.post(`/reviews/${reviewId}/finalize`, { rating: Number(rating) });
      toast.success("Review finalized — the employee has been notified");
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="review-dialog">
        {!review ? <Skeleton className="h-72 w-full rounded-none" /> : (
          <>
            <DialogHeader>
              <DialogTitle className="text-[16px] text-[var(--brand-primary)]">
                {review.employee_name} <span className="font-mono text-[12px] text-slate-400">{review.employee_id}</span>
              </DialogTitle>
              <p className="text-[12px] text-muted-foreground">
                {review.title} · {review.department} · {review.cycle_name} ·{" "}
                <span className="uppercase font-mono text-[10px]">{review.status.replace("_", " ")}</span>
              </p>
            </DialogHeader>

            {ev && (
              <div className="grid grid-cols-4 gap-2" data-testid="review-evidence">
                {[["KPI overall", ev.kpi_scores_1to5?.overall ?? "—"],
                  ["Target completion", `${ev.target_completion_pct}%`],
                  ["Peer collab avg", ev.peer_collaboration_avg ?? "—"],
                  ["Certs in period", ev.certifications_in_period]].map(([l, v]) => (
                  <div key={l} className="bg-slate-50 border border-border px-3 py-2">
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</p>
                    <p className="text-[14px] font-bold font-mono text-[var(--brand-primary)]">{v}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-4 bg-[var(--brand-primary)] text-white px-4 py-3">
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">Anchor (deterministic)</p>
                <p className="text-[18px] font-bold font-mono">{review.anchor_rating ?? "—"}</p>
              </div>
              <div className="border-l border-white/15 pl-4">
                <p className="text-[9px] font-semibold uppercase tracking-wider text-teal-400">
                  {finalized ? "Final rating" : "AI suggested"}
                </p>
                <p className="text-[18px] font-bold font-mono text-teal-400">
                  {content.rating ?? content.suggested_rating ?? "—"}
                </p>
              </div>
              {content.rating_justification && (
                <p className="text-[11px] text-slate-300 flex-1 leading-snug">{content.rating_justification}</p>
              )}
            </div>

            <Section label="Performance summary">
              {finalized ? (
                <p className="text-[13px] text-slate-700 leading-relaxed" data-testid="review-summary-text">{content.performance_summary}</p>
              ) : (
                <Textarea data-testid="review-summary-input" value={summary} rows={4}
                  onChange={(e) => setSummary(e.target.value)} className="rounded-none text-[13px]" />
              )}
            </Section>

            <div className="grid grid-cols-2 gap-5">
              <Section label="Strengths">
                <ul className="space-y-1.5">
                  {(content.strengths || []).map((s, i) => (
                    <li key={i} className="text-[12.5px] text-slate-700 pl-3 border-l-2 border-teal-600">{s}</li>
                  ))}
                </ul>
              </Section>
              <Section label="Growth areas">
                <ul className="space-y-1.5">
                  {(content.growth_areas || []).map((s, i) => (
                    <li key={i} className="text-[12.5px] text-slate-700 pl-3 border-l-2 border-amber-500">{s}</li>
                  ))}
                </ul>
              </Section>
            </div>

            {(content.development_actions || []).length > 0 && (
              <Section label="Recommended development actions">
                <div className="border border-border divide-y divide-border">
                  {content.development_actions.map((a, i) => (
                    <div key={i} className="px-3 py-2 flex items-center justify-between gap-3">
                      <p className="text-[12.5px] text-slate-700">{a.action}</p>
                      {a.course && (
                        <span className="text-[10px] font-mono bg-teal-50 border border-teal-600/30 text-teal-800 px-1.5 py-0.5 shrink-0">
                          {a.course}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {!finalized && (
              <div className="flex items-center justify-between border-t border-border pt-4">
                <div className="flex items-center gap-2">
                  <span className="text-[12px] text-slate-600">Final rating</span>
                  <Input data-testid="final-rating-input" type="number" min={1} max={5} step={0.5} value={rating}
                    onChange={(e) => setRating(e.target.value)} className="rounded-none w-20 h-8 font-mono text-right" />
                  <span className="text-[11px] text-muted-foreground">/ 5 — manager has final say</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={saveEdits} disabled={busy} data-testid="save-draft-btn"
                    className="rounded-none h-9 text-[12px]">Save edits</Button>
                  <Button onClick={finalize} disabled={busy || !rating} data-testid="finalize-btn"
                    className="rounded-none h-9 text-[12px] bg-teal-600 hover:bg-teal-700">Finalize review</Button>
                </div>
              </div>
            )}

            {review.audit_id && (
              <p className="text-[10px] font-mono text-muted-foreground">
                AI synthesis via masked gateway · model {review.model} · audit {review.audit_id} ·{" "}
                {review.entities_masked_count} entities tokenized · salary data never enters the prompt
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReviewDialog;
